import axiosInstance from './AxiosService';
import axios from 'axios';
import Cookies from 'js-cookie';

const HRService = {

    fetchHRRecordByEmail: async (email) => {
        try {
            console.log(`HR Service: Fetching HR record for email: ${email}`);
            const response = await axiosInstance.get(`hr/data/${email}/`);
            console.log(`HR Service: Success fetching record for ${email}:`, response.data);
            return response.data;
        } catch (error) {
            console.error(`HR Service: Error fetching HR record for ${email}:`, error);
            
            // If there's a 404, try fallback approach - search in all HR records
            if (error.response?.status === 404) {
                try {
                    console.log(`HR Service: Trying fallback - searching in all HR records for ${email}`);
                    const allRecords = await HRService.fetchAllHRRecords();
                    
                    // Case-insensitive search for the email
                    const normalizedEmail = email.toLowerCase().trim();
                    const matchingRecord = allRecords.find(record => 
                        record.EMAIL_ADDRESS && 
                        record.EMAIL_ADDRESS.toLowerCase().trim() === normalizedEmail
                    );
                    
                    if (matchingRecord) {
                        console.log(`HR Service: Found matching record via fallback for ${email}:`, matchingRecord);
                        return matchingRecord;
                    } else {
                        console.log(`HR Service: No matching record found in fallback search for ${email}`);
                        throw new Error('Record not found in fallback search');
                    }
                } catch (fallbackError) {
                    console.error(`HR Service: Fallback search failed for ${email}:`, fallbackError);
                    throw fallbackError;
                }
            }
            
            throw error;
        }
    },

    fetchAllHRRecords: async () => {
        try {
            console.log('HR Service: Fetching all HR records (including inactive)');
            // Add limit parameter to ensure we get all records and include_inactive for all user statuses
            const response = await axiosInstance.get(`hr/data/?limit=1000&include_inactive=true`);
            
            // Ensure we always return an array, even if the backend returns null or undefined
            if (!response.data) {
                console.warn('HR Service: Received empty data from server');
                return [];
            }
            
            // If response.data is not an array, wrap it in an array
            const records = Array.isArray(response.data) ? response.data : [response.data];
            
            // Filter out any null or undefined records
            const validRecords = records.filter(record => record !== null && record !== undefined);
            
            console.log(`HR Service: Successfully fetched ${validRecords.length} HR records (including inactive)`);
            return validRecords;
        } catch (error) {
            console.error('HR Service: Error fetching all HR records:', error);
            // Return empty array instead of throwing to prevent UI breaks
            return [];
        }
    },

    fetchHRRecordsForAudit: async () => {
        try {
            console.log('HR Service: Fetching all HR records for audit (including inactive)');
            
            // Try first with the audit-specific endpoint
            try {
                const auditResponse = await axiosInstance.get(`hr/data/audit/?limit=1000&include_all=true&include_inactive=true`);
                if (auditResponse.data) {
                    const records = Array.isArray(auditResponse.data) ? auditResponse.data : [auditResponse.data];
                    const validRecords = records.filter(record => record !== null && record !== undefined);
                    console.log(`HR Service: Successfully fetched ${validRecords.length} HR records from audit endpoint (including inactive)`);
                    return validRecords;
                }
            } catch (auditError) {
                console.warn('HR Service: Audit endpoint not available, falling back to standard endpoint');
            }
            
            // Fall back to the standard endpoint with parameters to get all records
            const response = await axiosInstance.get(`hr/data/?limit=1000&include_inactive=true&include_all=true`);
            
            if (!response.data) {
                console.warn('HR Service: Received empty data from server');
                return [];
            }
            
            // Process the data
            const records = Array.isArray(response.data) ? response.data : [response.data];
            const validRecords = records.filter(record => record !== null && record !== undefined);
            
            console.log(`HR Service: Successfully fetched ${validRecords.length} HR records for audit (including inactive)`);
            return validRecords;
        } catch (error) {
            console.error('HR Service: Error fetching HR records for audit:', error);
            
            // Last resort fallback - try with a direct axios call without interceptors
            try {
                console.log('HR Service: Attempting direct axios call for HR records (including inactive)');
                
                let token = localStorage.getItem('token');
                if (!token) {
                    token = Cookies.get('token');
                }
                
                if (!token) {
                    console.error('No authentication token available for fetching HR records');
                    return [];
                }
                
                // Add include_inactive=true to ensure we get all users
                const directResponse = await axios.get(
                    `${axiosInstance.defaults.baseURL}hr/data/?limit=1000&include_inactive=true&include_all=true`, 
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );
                
                if (!directResponse.data) {
                    return [];
                }
                
                const records = Array.isArray(directResponse.data) ? directResponse.data : [directResponse.data];
                const validRecords = records.filter(record => record !== null && record !== undefined);
                
                console.log(`HR Service: Successfully fetched ${validRecords.length} HR records via direct axios call (including inactive)`);
                return validRecords;
            } catch (directError) {
                console.error('HR Service: All methods failed to fetch HR records for audit');
                return [];
            }
        }
    },

    fetchActiveEmployees: async () => {
        try {
            console.log('HR Service: Fetching active employees...');
            
            // First try with the regular axiosInstance that has interceptors
            const response = await axiosInstance.get(`hr/data/active_employees/`);
            console.log('Active employees request completed using axiosInstance');
            return response.data;
        } catch (error) {
            console.error('Error fetching active employees with axiosInstance:', error);
            
            // If that fails, try with a direct call with the token from multiple sources
            try {
                let token = localStorage.getItem('token');
                if (!token) {
                    token = Cookies.get('token');
                }
                
                if (!token) {
                    console.error('No authentication token available for fetching active employees');
                    throw new Error('Authentication token not found');
                }
                
                // Create a one-time axios instance with the token
                const directResponse = await axios.get(`${axiosInstance.defaults.baseURL}hr/data/active_employees/`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                
                console.log('Active employees request completed using direct axios with token');
                return directResponse.data;
            } catch (directError) {
                console.error('Error fetching active employees with direct axios:', directError);
                throw directError;
            }
        }
    },

    fetchActiveEmployeesByEmail: async (email) => {
        try {
            console.log(`HR Service: Fetching active employee by email: ${email}`);
            
            // First try with the regular axiosInstance that has interceptors
            const response = await axiosInstance.get(`hr/data/active_employees_by_email/?ASSIGNED_TO=${encodeURIComponent(email)}`);
            console.log('Active employee by email request completed');
            return response.data;
        } catch (error) {
            console.error('Error fetching active employee by email:', error);
            
            // If that fails, try with a direct call with the token from multiple sources
            try {
                let token = localStorage.getItem('token');
                if (!token) {
                    token = Cookies.get('token');
                }
                
                if (!token) {
                    console.error('No authentication token available for fetching active employee by email');
                    throw new Error('Authentication token not found');
                }
                
                // Create a one-time axios instance with the token
                const directResponse = await axios.get(
                    `${axiosInstance.defaults.baseURL}hr/data/active_employees_by_email/?ASSIGNED_TO=${encodeURIComponent(email)}`, 
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );
                
                console.log('Active employee by email request completed using direct axios with token');
                return directResponse.data;
            } catch (directError) {
                console.error('Error fetching active employee by email with direct axios:', directError);
                throw directError;
            }
        }
    },

    fetchHRRecordById: async (id) => {
        try {
            console.log(`HR Service: Fetching HR record by ID: ${id}`);
            const response = await axiosInstance.get(`hr/data/id/${id}/`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching HR record by ID ${id}:`, error);
            throw error;
        }
    },

    fetchHRRecordByUUID: async (uuid) => {
        try {
            console.log(`HR Service: Fetching HR record by UUID: ${uuid}`);
            const response = await axiosInstance.get(`hr/data/uuid/${uuid}/`);
            console.log(`HR Service: Success fetching record for UUID ${uuid}:`, response.data);
            return response.data;
        } catch (error) {
            console.error(`HR Service: Error fetching HR record for UUID ${uuid}:`, error);
            
            // If there's a 404, try fallback approach - search in all HR records
            if (error.response?.status === 404) {
                try {
                    console.log(`HR Service: Trying fallback - searching in all HR records for UUID ${uuid}`);
                    const allRecords = await HRService.fetchAllHRRecords();
                    
                    // Search for the UUID in all records
                    const matchingRecord = allRecords.find(record => 
                        record.UUID && record.UUID === uuid
                    );
                    
                    if (matchingRecord) {
                        console.log(`HR Service: Found matching record via fallback for UUID ${uuid}:`, matchingRecord);
                        return matchingRecord;
                    } else {
                        console.log(`HR Service: No matching record found in fallback search for UUID ${uuid}`);
                        throw new Error('Record not found in fallback search');
                    }
                } catch (fallbackError) {
                    console.error(`HR Service: Fallback search failed for UUID ${uuid}:`, fallbackError);
                    throw fallbackError;
                }
            }
            
            throw error;
        }
    },

    // New methods for HR record manual upload
    downloadHRTemplate: async () => {
        try {
            console.log('HR Service: Downloading HR record template');
            const response = await axiosInstance.get('hr/upload/', {
                responseType: 'blob'
            });
            
            // Create a URL for the downloaded file
            const url = window.URL.createObjectURL(new Blob([response.data]));
            
            // Create a temporary link and trigger download
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'hr_upload_template.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            return true;
        } catch (error) {
            console.error('Error downloading HR template:', error);
            throw error;
        }
    },

    uploadHRRecords: async (formData) => {
        try {
            console.log('HR Service: Uploading HR records');
            const response = await axiosInstance.post('hr/upload/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error uploading HR records:', error);
            
            // Extract the most useful error information
            if (error.response) {
                // The server responded with an error status
                console.error('Server response error:', error.response.data);
                
                // If there's a detailed error message in the response
                if (error.response.data && error.response.data.error) {
                    if (typeof error.response.data.error === 'string') {
                        throw new Error(error.response.data.error);
                    } else if (error.response.data.detail) {
                        // If there are validation errors
                        if (Array.isArray(error.response.data.detail)) {
                            throw new Error(error.response.data.detail.join('\n'));
                        } else {
                            throw new Error(JSON.stringify(error.response.data.detail));
                        }
                    }
                }
                
                // Fallback to status text
                throw new Error(`Server error: ${error.response.status} ${error.response.statusText}`);
            } else if (error.request) {
                // The request was made but no response was received
                throw new Error('No response received from server. Please check your network connection.');
            } else {
                // Something happened in setting up the request
                throw new Error(error.message || 'Unknown error occurred');
            }
        }
    },

    fetchHRImportLogs: async () => {
        try {
            console.log('HR Service: Fetching HR import logs');
            const response = await axiosInstance.get('hr-job-log/');
            return response.data;
        } catch (error) {
            console.error('Error fetching HR import logs:', error);
            throw error;
        }
    },

    // Fetch HR change history records with optional filtering
    fetchHRChangeHistory: async (filters = {}) => {
        try {
            let url = '/admin/hr/changes/';
            
            // Add query parameters for filtering
            const params = new URLSearchParams();
            if (filters.user_id) params.append('user_id', filters.user_id);
            if (filters.email) params.append('email', filters.email);
            if (filters.days) params.append('days', filters.days);
            if (filters.change_type) params.append('change_type', filters.change_type);
            if (filters.page) params.append('page', filters.page);
            if (filters.page_size) params.append('page_size', filters.page_size);
            
            if (params.toString()) {
                url += `?${params.toString()}`;
            }
            
            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('Error fetching HR change history:', error);
            throw error;
        }
    },

    // Fetch HR change history summary statistics
    fetchHRChangeHistorySummary: async () => {
        try {
            const response = await axiosInstance.get('/admin/hr/changes/summary/');
            return response.data;
        } catch (error) {
            console.error('Error fetching HR change history summary:', error);
            throw error;
        }
    },

    // Specific method for audit reporting - explicitly fetches both active and inactive users
    fetchHRRecordsForReporting: async () => {
        try {
            console.log('HR Service: Fetching all HR records for reporting (both active and inactive)');
            
            // Try to fetch with specific reporting endpoint (if it exists on the backend)
            try {
                const reportingUrl = `hr/data/reporting/?limit=2000&include_inactive=true&include_all=true`;
                console.log(`HR Service: Trying reporting endpoint with URL: ${axiosInstance.defaults.baseURL}${reportingUrl}`);
                
                const reportResponse = await axiosInstance.get(reportingUrl);
                if (reportResponse.data) {
                    const records = Array.isArray(reportResponse.data) ? reportResponse.data : [reportResponse.data];
                    
                    // Check active/inactive distribution
                    const activeCount = records.filter(r => r.STATUS?.toLowerCase() === 'active').length;
                    const inactiveCount = records.filter(r => 
                        r.STATUS?.toLowerCase() === 'inactive' || 
                        r.STATUS?.toLowerCase() === 'terminated'
                    ).length;
                    const unknownCount = records.length - activeCount - inactiveCount;
                    
                    console.log(`HR Service: Records from reporting endpoint - Total: ${records.length}, Active: ${activeCount}, Inactive: ${inactiveCount}, Unknown: ${unknownCount}`);
                    
                    return records;
                }
            } catch (reportError) {
                console.warn('HR Service: Reporting endpoint not available, error:', reportError.message);
                console.log('HR Service: Trying standard endpoint with include_inactive parameter');
            }
            
            // Higher limit (2000) to ensure we get all records
            const standardUrl = `hr/data/?limit=2000&include_inactive=true&include_all=true&for_reporting=true`;
            console.log(`HR Service: Trying standard endpoint with URL: ${axiosInstance.defaults.baseURL}${standardUrl}`);
            
            const response = await axiosInstance.get(standardUrl);
            
            if (!response.data) {
                throw new Error('No data received from server');
            }
            
            const records = Array.isArray(response.data) ? response.data : [response.data];
            
            // Log the status distribution to help troubleshoot
            const activeCount = records.filter(r => r.STATUS?.toLowerCase() === 'active').length;
            const inactiveCount = records.filter(r => r.STATUS?.toLowerCase() === 'inactive' || r.STATUS?.toLowerCase() === 'terminated').length;
            const otherStatusCount = records.length - activeCount - inactiveCount;
            
            console.log(`HR Service: Fetched ${records.length} total records - Status breakdown: 
                Active: ${activeCount}, 
                Inactive/Terminated: ${inactiveCount}, 
                Other/Unknown Status: ${otherStatusCount}`
            );
            
            // If no inactive records found when we explicitly requested them, log a warning
            if (inactiveCount === 0 && records.length > 0) {
                console.warn('HR Service: WARNING - No inactive records found despite include_inactive=true parameter.');
                console.warn('HR Service: This likely indicates a backend filtering issue preventing inactive records from being included.');
                console.warn('HR Service: Check your backend API implementation for any hardcoded active-only filters.');
            }
            
            return records;
        } catch (error) {
            console.error('HR Service: Error fetching HR records for reporting:', error);
            throw error;
        }
    },

    // Direct approach that explicitly fetches both active and inactive records
    // This is a last resort method that will make separate calls if needed
    fetchAllHRRecordsIncludingInactive: async () => {
        try {
            console.log('HR Service: Explicitly fetching BOTH active and inactive records');
            
            // First try to get all records with the explicit parameter
            try {
                const apiUrl = `hr/data/?limit=5000&include_inactive=true&include_all=true&bypass_filters=true`;
                console.log(`HR Service: Attempting to fetch all records with URL: ${axiosInstance.defaults.baseURL}${apiUrl}`);
                
                const allResponse = await axiosInstance.get(apiUrl);
                
                if (allResponse.data && Array.isArray(allResponse.data) && allResponse.data.length > 0) {
                    const records = allResponse.data;
                    
                    // Check if we have both active and inactive records
                    const hasActive = records.some(r => r.STATUS?.toLowerCase() === 'active');
                    const hasInactive = records.some(r => 
                        r.STATUS?.toLowerCase() === 'inactive' || 
                        r.STATUS?.toLowerCase() === 'terminated'
                    );
                    
                    // Log the status distribution to help debug
                    const statusCounts = {};
                    records.forEach(record => {
                        const status = (record.STATUS || 'Unknown').toLowerCase();
                        statusCounts[status] = (statusCounts[status] || 0) + 1;
                    });
                    console.log('HR Service: Status distribution from first attempt:', statusCounts);
                    
                    if (hasActive && hasInactive) {
                        console.log(`HR Service: Successfully fetched both active and inactive records in a single call (${records.length} total)`);
                        return records;
                    } else {
                        console.log(`HR Service: First attempt didn't get both active and inactive records. Active: ${hasActive}, Inactive: ${hasInactive}`);
                    }
                }
            } catch (error) {
                console.warn('HR Service: First attempt failed:', error);
                console.log('HR Service: Error details:', {
                    status: error.response?.status, 
                    statusText: error.response?.statusText,
                    message: error.message
                });
            }
            
            // If we're here, the first approach didn't work. Try separate calls for active and inactive
            console.log('HR Service: Trying separate calls for active and inactive records');
            
            // Get active records
            console.log('HR Service: Fetching active records with URL:', `${axiosInstance.defaults.baseURL}hr/data/active_employees/?limit=2500`);
            const activeResponse = await axiosInstance.get(`hr/data/active_employees/?limit=2500`);
            const activeRecords = Array.isArray(activeResponse.data) ? activeResponse.data : [];
            console.log(`HR Service: Fetched ${activeRecords.length} active records`);
            
            // Get inactive records - try multiple possible endpoints
            let inactiveRecords = [];
            
            try {
                // Try specific inactive endpoint if it exists
                console.log('HR Service: Attempting to fetch inactive records with URL:', `${axiosInstance.defaults.baseURL}hr/data/inactive_employees/?limit=2500`);
                const inactiveResponse = await axiosInstance.get(`hr/data/inactive_employees/?limit=2500`);
                inactiveRecords = Array.isArray(inactiveResponse.data) ? inactiveResponse.data : [];
                console.log(`HR Service: Fetched ${inactiveRecords.length} inactive records from dedicated endpoint`);
            } catch (e) {
                console.log('HR Service: Dedicated inactive endpoint failed, error:', e.message);
                // If no dedicated inactive endpoint, try filter parameter
                try {
                    console.log('HR Service: Trying status filter with URL:', `${axiosInstance.defaults.baseURL}hr/data/?status=inactive&limit=2500`);
                    const filteredResponse = await axiosInstance.get(`hr/data/?status=inactive&limit=2500`);
                    inactiveRecords = Array.isArray(filteredResponse.data) ? filteredResponse.data : [];
                    console.log(`HR Service: Fetched ${inactiveRecords.length} inactive records using status filter`);
                } catch (e2) {
                    console.error('HR Service: Failed to fetch inactive records:', e2);
                }
            }
            
            // Combine records, ensuring no duplicates by email
            const allRecords = [...activeRecords];
            
            // Add inactive records that aren't already in active records
            const activeEmails = new Set(activeRecords.map(r => r.EMAIL_ADDRESS?.toLowerCase()));
            
            inactiveRecords.forEach(record => {
                if (record.EMAIL_ADDRESS && !activeEmails.has(record.EMAIL_ADDRESS.toLowerCase())) {
                    allRecords.push(record);
                }
            });
            
            console.log(`HR Service: Combined ${activeRecords.length} active and ${inactiveRecords.length} inactive records for a total of ${allRecords.length} unique records`);
            
            return allRecords;
        } catch (error) {
            console.error('HR Service: Error in explicit fetch of active/inactive records:', error);
            return [];
        }
    }
}

export default HRService;
